import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useGetFeedbackInboxCountsQuery } from 'api/feedbackInbox'
import {
  useGetActivityMetricsQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import { isFetchBaseQueryError } from 'api/utils'
import Badge from 'components/Badge'
import CountBadge from 'components/CountBadge'
import PageHeader from 'components/PageHeader'
import Spinner from 'components/Spinner'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { useLiveUpdatesAccess } from 'hooks/useLiveUpdatesAccess'
import { useOloWebhookStatus } from 'hooks/useOloWebhookStatus'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo, useRef } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { push, replace } from 'react-router-redux'
// Cross-scene mount exception (docs/05-frontend-components.md "Architecture —
// decided", 11-chassis-plan.md §0 "Location"): same one-off exception as
// `scenes/OrderFeedback`'s (documented at `FeedbackInbox/index.tsx`'s
// own "Cross-scene mount exception") — this is PR 08's RAI-side mount.
import FeedbackInbox from 'scenes/FeedbackInbox'
import HostFlexShell from 'scenes/FeedbackInbox/HostFlexShell'
import { useInboxMountState } from 'scenes/FeedbackInbox/useInboxMountState'
import { useScrollInboxIntoView } from 'scenes/FeedbackInbox/useScrollInboxIntoView'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { OLO_PROVIDER_KEY } from 'utilities/oloWebhookStatus'
import type { OloWebhookPanel } from 'utilities/oloWebhookStatus'
import Activity from '../Activity'
import TabLockedMessage from '../components/TabLockedMessage'
import Overview from '../Overview'
import PromoLanding from '../PromoLanding'
import { isRecoveryActive, isRecoveryConfigured } from '../recoveryStatus'
import {
  ACTIVATION_PATH,
  ESCALATIONS_PATH,
  LIVE_UPDATES_OLO_SETUP_PATH,
  SETTINGS_PATH,
} from '../routes'
import StandaloneSimulator from '../Sandbox/StandaloneSimulator'
import Settings from '../Settings'
import { resolveCoverage, resolveDeploymentStage } from './deploymentStage'
import GetStarted from './GetStarted'

const t = buildTranslate('recovery_ai')

type TabKey = 'overview' | 'escalations' | 'agent_tuning' | 'settings'

// Overview is the default tab and owns the bare /recovery_ai path. Settings
// keeps /recovery_ai/configuration so existing links and the wizard's
// post-completion redirect keep working. R12 (F4) collapses the bar from 5 tabs
// to 4: Conversation Preview folds into the new Agent Tuning tab (the live
// agent simulator's home) and Banned Customers demotes into Settings (WP5
// re-homes the list). The /recovery_ai/agent_tuning route name pends product
// sign-off; it's localized to TAB_PATHS + activeTabFromPath so a rename is cheap.
const TAB_PATHS: Record<TabKey, string> = {
  overview: '/recovery_ai',
  escalations: ESCALATIONS_PATH,
  agent_tuning: '/recovery_ai/agent_tuning',
  settings: SETTINGS_PATH,
}

export function activeTabFromPath(pathname: string): TabKey {
  if (pathname.startsWith(ESCALATIONS_PATH)) return 'escalations'
  if (pathname.startsWith('/recovery_ai/agent_tuning')) return 'agent_tuning'
  if (pathname.startsWith(SETTINGS_PATH)) return 'settings'
  return 'overview'
}

// Active-location count (N) fallback — an em-dash only while metrics are
// in-flight (never a literal 0, which would falsely read as "active at no
// locations"). The backend now exposes the count via metrics.activeLocationsCount.
const ACTIVE_LOCATIONS_PLACEHOLDER = '—'

// Config-load error surface metrics (mirrors ConfiguredSummary's retry region).
const LOAD_ERROR_GAP = '16px'
const LOAD_ERROR_PADDING = '48px 24px'

// Does a failed config GET mean "this merchant has no configuration" rather than
// "we don't know"? GET recovery/configuration `find_or_initialize`s a 200 DEFAULT
// config for a merchant who never onboarded (see the Wizard's hydration note), so
// "no configuration" arrives as DATA, never as an error. A 404 therefore means the
// RESOURCE is unavailable — the server-side recovery-ai flag is off, or FE/BE
// deploy skew — which is still an unentitled merchant, so it keeps the marketing
// page (product spec: no configuration ⇒ promo page).
//
// Every OTHER error (5xx, network) with no cached config is INDETERMINATE: an
// ACTIVE merchant would otherwise be shown the marketing page for a product they
// already pay for, because isRecoveryActive fails closed on undefined data. Those
// get the retry surface instead.
const isMissingConfigError = (error: unknown): boolean =>
  isFetchBaseQueryError(error) && error.status === 404

const Landing = ({ location }: RouteComponentProps) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // `recovery-ai-activation-landing` used to be read here too, for AI-693's
  // soft-lock redirect. Master removed that redirect (and the flag read with
  // it), so only the inbox flag remains.
  const unifiedInboxEnabled = useFlag(
    'recovery-ai-unified-feedback-inbox',
    false
  )
  const { setupState, collecting, writtenFeedbackEnabled } =
    useInboxMountState(unifiedInboxEnabled)
  const activeTab = activeTabFromPath(location.pathname)
  // Activation is signalled by `onboarding_completed` on the recovery config
  // (flipped by the wizard's Activation step). Until it's true we show
  // GetStarted instead of the live dashboard. isConfigured / isActive are the
  // shared recoveryStatus predicates (single source with the Wizard re-entry
  // guard). "Active" fails closed on a null/unknown mode. Only fetch the count
  // when active.
  //
  // /recovery_ai is open to ANY authenticated merchant user when RecoveryAI is
  // not set up for the merchant (see the route gate in ../index.tsx), so this
  // page is now reached by users who have no merchant_settings permission. For
  // them GET recovery/configuration is a guaranteed 403 — and 403 is not a 404,
  // so isMissingConfigError wouldn't excuse it and the "couldn't load" retry
  // panel would render over the marketing page. SKIP the request instead of
  // special-casing its error: no wasted 403 per page load, and no error surface.
  // RBAC subject, stated plainly — no legacy fallback arm (see CLAUDE.md).
  const canAccessConsole = useAbility().can('access', 'MerchantSettings')
  const {
    data: config,
    error: configError,
    isError: isConfigError,
    isLoading: isConfigLoading,
    refetch: refetchConfig,
  } = useGetRecoveryConfigQuery(undefined, { skip: !canAccessConsole })
  // Gate the error surface on NEVER-loaded (error AND no data), not isError alone:
  // RTK RETAINS the last-good `data` across a failed REFETCH (the config
  // invalidation a Settings save fires), so isError can be true on a console that
  // hydrated fine — tearing it down then would be a false positive. Same seam as
  // the Wizard's `configNeverLoaded`.
  const showConfigLoadError =
    isConfigError && !config && !isMissingConfigError(configError)
  const isConfigured = isRecoveryConfigured(config)
  const isActive = isRecoveryActive(config)
  // Pre-activation sandbox access: a configured-but-off merchant may test in the
  // simulator before paying (D2). StandaloneSimulator has no isActive dependency;
  // the config EDITORS stay isActive-gated.
  const canTestInSandbox = isConfigured
  // Pre-activation (configured-but-off) ONLY agent_tuning is reachable — the
  // soft-lock redirect below bounces every other tab straight to the activation
  // CTA. Disable those tabs rather than render trapdoors that lie about where
  // they go (#3).
  const lockNonAgentTuningTabs = isConfigured && !isActive
  // AI-932 / AI-892: exempts the agent_tuning tab from the promo gate below so a
  // configured-but-off merchant who followed ConfiguredSummary's "Test your agent
  // in sandbox" link here (the PREPARER branch of that fork — an editor goes to the
  // wizard Testing step instead) reaches the live simulator rather than the
  // marketing page. Requires lockNonAgentTuningTabs (isConfigured && !isActive), so
  // it fires ONLY pre-activation. ActivationSuccess's "Keep testing" also pushes
  // here but runs post-activation (isActive), so the ordinary isActive path serves
  // it — not this carve-out.
  const showPreActivationAgentTuning =
    lockNonAgentTuningTabs && activeTab === 'agent_tuning'

  // Same page treatment as OrderFeedback's Feedback tab: opening the
  // Escalations inbox scrolls the page past the title. `isConfigured` gates
  // the timing, not the outcome: on a direct load the console (and the
  // PageHeader this ref points into) only renders once the config arrives, so
  // the hook must turn active on that render rather than during the spinner.
  const descriptionRef = useRef<HTMLDivElement>(null)
  const isInboxMounted =
    activeTab === 'escalations' && unifiedInboxEnabled && isConfigured
  useScrollInboxIntoView(descriptionRef, isInboxMounted)
  const { data: metrics } = useGetActivityMetricsQuery(undefined, {
    skip: !isActive,
  })
  const { total: totalLocations, active: activeLocations } = resolveCoverage({
    totalLocationsCount: metrics?.totalLocationsCount,
    activeLocationsCount: metrics?.activeLocationsCount,
    guestFacingTotalLocationsCount: metrics?.guestFacingTotalLocationsCount,
    guestFacingActiveLocationsCount: metrics?.guestFacingActiveLocationsCount,
  })

  // Parent-tab escalation badge (same metric as the Activity sub-tab). When
  // !isActive the metrics query is skipped -> undefined -> 0 -> CountBadge null.
  const escalationCount = metrics?.actionRequiredCount ?? 0

  // Fixed status:'open' — badge reads "needs attention", not the inbox's own
  // filter state; same shape as FeedbackInbox's own filter-bar query on first
  // load, so RTK dedupes into one cache entry with defaults.
  const { data: unifiedCounts } = useGetFeedbackInboxCountsQuery(
    { status: 'open' },
    { skip: !unifiedInboxEnabled || !canAccessConsole }
  )
  const badgeCount = unifiedInboxEnabled
    ? unifiedCounts?.recoveryAi ?? 0
    : escalationCount

  // AI-1295 (FR-39), design revision: an Olo merchant whose webhooks need
  // setup/attention sees the same OloWebhookBanner Live updates shows, now on
  // the Overview tab only (not every tab — see oloWebhookPanel below).
  // Explicit isActive — the agent_tuning carve-out reaches the console JSX
  // with isActive=false. `enabled` skips both webhook queries for non-Olo/
  // inactive/ineligible merchants; undefined (queries in flight, D8) renders
  // nothing.
  //
  // Gated on `useLiveUpdatesAccess({ requireFlag: false }).eligible` (the FR-76
  // summary endpoint), not the merchant-level `ordering_enabled` field: the
  // CTA (LIVE_UPDATES_OLO_SETUP_PATH) redirects home when that hook's
  // `eligible` is false (LiveUpdates/index.tsx), so gating this banner on a
  // different signal than the page it links to risked showing a banner whose
  // only CTA is a dead end. `requireFlag: false` matches how the scene itself
  // calls the hook — the LD flag only controls nav visibility, not scene/
  // summary eligibility (see the hook's own doc).
  const merchant = useCurrentMerchant()
  const isOloOrdering = merchant?.ordering_provider === OLO_PROVIDER_KEY
  const { eligible: liveUpdatesEligible } = useLiveUpdatesAccess({
    requireFlag: false,
  })
  const oloWebhookEnabled = isActive && isOloOrdering && liveUpdatesEligible
  const { connectionState, lastReceivedAt, needsReconnect } =
    useOloWebhookStatus(oloWebhookEnabled)
  const onManageOloWebhook = useCallback(
    () => dispatch(push(LIVE_UPDATES_OLO_SETUP_PATH)),
    [dispatch]
  )
  // Undefined (no banner) unless the connection needs setup or attention —
  // a healthy, non-stale connection renders nothing (mirrors Live updates'
  // own OloWebhookBanner gating). Reuses OloWebhookPanel (utilities/
  // oloWebhookStatus) since that's exactly this shape.
  const oloWebhookPanel: OloWebhookPanel | undefined = useMemo(
    () =>
      oloWebhookEnabled &&
      connectionState &&
      (connectionState !== 'connected' || needsReconnect)
        ? {
            connectionState,
            lastReceivedAt,
            needsReconnect,
            onManage: onManageOloWebhook,
          }
        : undefined,
    [
      oloWebhookEnabled,
      connectionState,
      lastReceivedAt,
      needsReconnect,
      onManageOloWebhook,
    ]
  )

  // AI-241: the Overview tab picks its content from guest-facing deployment —
  // the `full` dashboard once every guest-facing (standard-visibility) store is
  // on, `pilot` while only some are, and an empty state (`none`) when none are.
  // resolveCoverage supplies the counts (guest-facing, else merchant-visible),
  // shared with the header and billboard below so all three agree. Metrics are
  // only fetched when active (skip: !isActive), so an inactive or still-loading
  // merchant reads as the conservative `pilot`. The LIVE render of either
  // dashboard also requires the BE to allowlist looker-dashboards-551 / -552 on
  // the `looker-embed-paths` LD flag (see Overview) — until then it shows
  // LookerError.
  const deploymentStage = resolveDeploymentStage({
    totalLocationsCount: totalLocations,
    activeLocationsCount: activeLocations,
    guestFacingTotalLocationsCount: metrics?.guestFacingTotalLocationsCount,
    guestFacingActiveLocationsCount: metrics?.guestFacingActiveLocationsCount,
  })

  // FR-01 partial-state billboard (AI-241): surface "Turn on RecoveryAI at
  // more locations" only when active + coverage < 100% + a known count. Full
  // (active === total) and loading (null) yield undefined → Overview renders no
  // billboard. Uses the same resolveCoverage counts as deploymentStage and the
  // header, so they agree; inherits AI-208's provisional denominator (fine for
  // a strict `>` check).
  const remainingLocations =
    isActive &&
    totalLocations != null &&
    activeLocations != null &&
    totalLocations > activeLocations
      ? totalLocations - activeLocations
      : undefined

  // role="status" + a visually-hidden prefix so screen readers announce the
  // pill with context ("RecoveryAI status: Active"), not a bare "Active" run
  // straight after the title (AI-36 review).
  const statusPill = isActive ? (
    <span role="status">
      <span className="sr-only">{t('header.status_label')} </span>
      <Badge
        className="nephritis-20-bkg nephritis-50"
        value={t('header.status_active')}
      />
    </span>
  ) : undefined

  const locationSubtitle =
    isActive && totalLocations != null
      ? t(
          totalLocations === 1
            ? 'header.locations_one'
            : 'header.locations_other',
          {
            active: activeLocations ?? ACTIVE_LOCATIONS_PLACEHOLDER,
            count: totalLocations,
          }
        )
      : undefined

  const handleSelect = useCallback(
    (tab: string | null) => {
      if (tab && tab !== activeTab && tab in TAB_PATHS) {
        dispatch(replace(TAB_PATHS[tab as TabKey]))
      }
    },
    [dispatch, activeTab]
  )

  // No console permission ⇒ the marketing page, always — this is the surface the
  // ungated route exists to serve. Stated as its own return rather than left to
  // fall through the gates below: the skipped config query happens to yield the
  // same outcome (uninitialized ⇒ isLoading false, data undefined ⇒ not active ⇒
  // PromoLanding), but that is a coincidence of RTK's skip semantics, not a
  // guarantee worth resting a permission boundary on. Placed after every hook so
  // the early return never changes hook order.
  if (!canAccessConsole) {
    return <PromoLanding />
  }

  // Promo-landing gate. Until RecoveryAI is ACTIVE for this merchant, the static
  // marketing page supersedes the console — no PageHeader, no tab bar, no
  // GetStarted — EXCEPT showPreActivationAgentTuning (AI-932 / AI-892): a
  // configured-but-off merchant who followed ConfiguredSummary's "Test your agent
  // in sandbox" link here (its preparer branch — see that fork) falls through
  // instead, to the tab bar below (other three tabs locked via
  // lockNonAgentTuningTabs). isRecoveryActive fails closed,
  // so a missing config, a 404 (unentitled merchant), and an unknown
  // operating_mode all land here — but a HARD load failure with no cached config
  // does not (it takes the retry surface above, since it can't be distinguished
  // from an active merchant). Placed after every hook so the early return never
  // changes hook order; the metrics query stays mounted behind its own
  // `skip: !isActive`.
  //
  // While the config is still in flight neither surface is correct yet — render the
  // spinner rather than flashing tabs or promo copy and then swapping.
  if (isConfigLoading) {
    return (
      <Spinner isLoading size="4x" className="text-center margin-top-huge" />
    )
  }

  // A hard config failure with nothing cached is NOT "inactive" — showing the promo
  // page here would hide a paying merchant's live console behind a transient 5xx.
  // Surface the failure with an in-place retry instead (see isMissingConfigError
  // for why a 404 still falls through to the promo page below).
  if (showConfigLoadError) {
    return (
      <div
        className={`d-flex flex-column align-items-center ${css({
          gap: LOAD_ERROR_GAP,
          padding: LOAD_ERROR_PADDING,
          textAlign: 'center',
        })}`}
        role="alert"
      >
        <Text.BodyText3 color="grey70">
          {t('promo.load_error.message')}
        </Text.BodyText3>
        <Button kind="secondary" onClick={refetchConfig}>
          {t('promo.load_error.retry')}
        </Button>
      </div>
    )
  }

  if (!isActive && !showPreActivationAgentTuning) {
    return <PromoLanding />
  }

  // AI-693 soft-lock guard: Finalize completes onboarding but leaves the config
  // off, so a configured-but-off merchant is parked between Finalize and the
  // pricing-modal enable. Route them to ConfiguredSummary (the pricing/activate
  // CTA) rather than the isActive-gated TabLockedMessage, which has no in-app
  // way to turn RecoveryAI back on. Placed after every hook so the redirect
  // never changes the hook call order.
  // NB: showPreActivationAgentTuning is the only way past the gate above for a
  // configured-but-off merchant, and it requires activeTab === 'agent_tuning' — so
  // this redirect's own condition (activeTab !== 'agent_tuning') can never be true
  // here. That is intentional, NOT a regression: post-#3913 a configured-but-off
  // merchant on any OTHER tab sees the promo page by design, not this activation
  // CTA. Kept intact (rather than deleted) as a documented invariant and a
  // one-edit restore point should the promo gate ever be narrowed.
  if (isConfigured && !isActive && activeTab !== 'agent_tuning') {
    return <Redirect to={ACTIVATION_PATH} />
  }

  return (
    <PageHeader
      title={t('page_title')}
      topComponent={statusPill}
      description={locationSubtitle}
      descriptionRef={descriptionRef}
    >
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <Tab.Container
        activeKey={activeTab}
        transition={false}
        onSelect={handleSelect}
      >
        <TabNav>
          <TabItem
            state="overview"
            title={t('tabs.overview')}
            disabled={lockNonAgentTuningTabs}
          />
          <TabItem
            state="escalations"
            title={t('tabs.escalations')}
            disabled={lockNonAgentTuningTabs}
            // Renders unconditionally now that the single-table redesign removed
            // the Activity sub-tab's inner badge (item 5) — this parent badge is
            // the sole source of the count, so there's no double-count to suppress.
            // `badgeCount` is the unified-inbox count when the flag is on
            // (PR 08), the legacy escalation-metrics count otherwise.
            badge={
              <CountBadge
                count={badgeCount}
                srLabel={t(
                  badgeCount === 1
                    ? 'activity.tabs.needs_attention_badge_one'
                    : 'activity.tabs.needs_attention_badge_other',
                  { count: badgeCount }
                )}
                className="ml-xs"
              />
            }
          />
          <TabItem state="agent_tuning" title={t('tabs.agent_tuning')} />
          <TabItem
            state="settings"
            title={t('tabs.settings')}
            disabled={lockNonAgentTuningTabs}
          />
        </TabNav>
        {/* Same Tab.Content classes as Order Feedback's, so both inbox hosts
            match: `pb-l` leaves a gutter below the inbox, and `h-100` gives
            the page the scroll room past the title that
            useScrollInboxIntoView needs. Only while the inbox is mounted:
            `h-100` would force every other RAI tab to fill the viewport. */}
        <Tab.Content
          className={isInboxMounted ? 'pb-l h-100 grey-05-bkg' : undefined}
        >
          <TabPane state="overview">
            {activeTab === 'overview' &&
              (isConfigured ? (
                <Overview
                  deploymentStage={deploymentStage}
                  remainingLocations={remainingLocations}
                  oloWebhook={oloWebhookPanel}
                />
              ) : (
                <GetStarted />
              ))}
          </TabPane>
          {/* The inbox brings its own spacing (INBOX_PAGE_PADDING_PX), so the
              default 48px py-xl would stack on top and eat viewport height. */}
          <TabPane
            state="escalations"
            paddingY={unifiedInboxEnabled ? 'none' : 'xl'}
          >
            {/* One table, no sub-tabs (RAI redesign): the Escalations tab is the
                recovery-case queue. The directory is still named Activity/ — the
                user-visible copy says "Review Escalations". Rename deferred to keep
                this diff (and the jest.mock path churn) small. Tab label stays
                "Escalations" under the unified-inbox flag (D1) — only the body
                swaps to FeedbackInbox; `collecting` has no gating role on this
                host (D1 table), forwarded only as a required prop. */}
            {activeTab === 'escalations' &&
              (isConfigured ? (
                unifiedInboxEnabled ? (
                  // `HostFlexShell` gives the inbox a flex column to
                  // fill — it is viewport-bound rather than
                  // `calc(100vh - magic)` (docs/06-ux-rules.md "Height
                  // model"). Scoped to this pane rather than wrapped
                  // around the whole page so the other tabs keep their
                  // current layout untouched — the page-wide wrapper
                  // this replaced also forced every other RAI tab to be
                  // >=100vh.
                  <HostFlexShell>
                    <FeedbackInbox
                      initialParams={{
                        kind: 'recovery_ai',
                        status: 'open',
                      }}
                      setupState={setupState}
                      collecting={collecting}
                      writtenFeedbackEnabled={writtenFeedbackEnabled}
                    />
                  </HostFlexShell>
                ) : (
                  <Activity />
                )
              ) : (
                <GetStarted />
              ))}
          </TabPane>
          <TabPane state="agent_tuning">
            {/* Agent Tuning hosts the live agent simulator (FE-1's
                StandaloneSimulator — the real free-chat "test your own" shell),
                which now also mounts the #3684 config-modal chevron rows in its
                right column. */}
            {activeTab === 'agent_tuning' &&
              (canTestInSandbox ? (
                // The config EDITORS (AgentTuningConfigModals — approved rewards,
                // recovery generosity, brand voice) render beside the phone in BOTH
                // the pre-activation carve-out and the active console, so the tab
                // looks identical either way. The wizard mounts the same editors
                // pre-activation and Save commits live, so this is a proven-safe
                // path — a configured-but-off merchant must be able to tune before
                // paying. The card self-manages its open row. "Save and test" is
                // hidden on this card's modals (StandaloneSimulator sets
                // hideSaveAndTest) — this simulator IS the target, already on
                // screen, so there is nowhere left to route.
                <StandaloneSimulator embedded />
              ) : (
                // Defense-in-depth, currently unreachable: the promo gate only lets
                // an isConfigured merchant reach this body (isActive, or the
                // agent_tuning carve-out, both imply canTestInSandbox), so this
                // branch never renders today. Kept so a future gate change that lets
                // a not-configured merchant land here still locks the tab rather than
                // half-rendering the simulator against a config that isn't running.
                <TabLockedMessage />
              ))}
          </TabPane>
          <TabPane state="settings">
            {/* AI-27: Settings tab now hosts the vertical sub-tab hub. The legacy
                Configuration read-view stays in-tree until all panes land.
                Gated on isActive: an inactive RecoveryAI locks the whole hub
                (short-circuits every pane at once) behind the enable message. */}
            {activeTab === 'settings' &&
              (isActive ? <Settings /> : <TabLockedMessage />)}
          </TabPane>
        </Tab.Content>
      </Tab.Container>
    </PageHeader>
  )
}

export default Landing
